<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import GwIcon from '@/components/GwIcon.vue'
import { computed, onMounted, nextTick } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import IncludedBackofficeCallout from '@/Pages/Web/components/IncludedBackofficeCallout.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import { useScrollReveal } from '@/composables/useScrollReveal.js'

const { get, locale, webRoute, alternateLocales } = useWebTranslations()
const { refresh } = useScrollReveal()

const seoTitle = computed(() => get('pages.ecommerce.seo_title'))
const seoDescription = computed(() => get('pages.ecommerce.seo_description'))
const canonicalPath = computed(() => webRoute('web.ecommerce'))
const pageAlternateLocales = computed(() => alternateLocales('web.ecommerce'))

const featuresHref = computed(() => `${webRoute('web.features')}#webstore`)

const valueProps = [
  {
    key: 'payments',
    icon: 'lock',
  },
  {
    key: 'shipping',
    icon: 'truck',
  },
  {
    key: 'mobile',
    icon: 'device-mobile',
  },
]

const trustItems = ['no_coding', 'fast_setup', 'all_in_one']

const featureCards = [
  {
    key: 'products',
    accent: '#FF43A0',
    accentSoft: 'rgba(255,67,160,0.12)',
    icon: 'shopping-cart',
  },
  {
    key: 'payments',
    accent: '#A855F7',
    accentSoft: 'rgba(168,85,247,0.12)',
    icon: 'credit-card',
  },
  {
    key: 'shipping',
    accent: '#F59754',
    accentSoft: 'rgba(245,151,84,0.14)',
    icon: 'truck',
  },
  {
    key: 'orders',
    accent: '#22A06B',
    accentSoft: 'rgba(34,160,107,0.12)',
    icon: 'bag',
  },
  {
    key: 'customers',
    accent: '#3B82F6',
    accentSoft: 'rgba(59,130,246,0.12)',
    icon: 'user',
  },
]

const storeProducts = [
  { name: 'Chair', price: '€189', tone: '#F8B4C8' },
  { name: 'Vase', price: '€48', tone: '#C4B5FD' },
  { name: 'Lamp', price: '€96', tone: '#FCD34D' },
  { name: 'Clock', price: '€72', tone: '#93C5FD' },
]

const editorItems = ['edit_text', 'edit_image', 'change_color', 'add_element']

onMounted(() => {
  nextTick(() => refresh())
})
</script>

<template>
  <SEOHead
    :title="seoTitle"
    :description="seoDescription"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <section class="relative overflow-hidden bg-[#F7F4FB]">
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none">
      <div
        class="absolute -top-24 -right-20 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-40"
        style="background: radial-gradient(circle, rgba(241,0,254,0.20) 0%, transparent 70%);"
      ></div>
      <div
        class="absolute -bottom-28 -left-24 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-35"
        style="background: radial-gradient(circle, rgba(245,151,84,0.24) 0%, transparent 70%);"
      ></div>
    </div>

    <!-- Hero -->
    <div class="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-10 pb-14 lg:pt-14 lg:pb-20">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">

        <!-- Copy -->
        <div class="lg:col-span-5 reveal">
          <span
            class="inline-flex items-center rounded-full px-3.5 py-1.5 text-[0.7rem] font-semibold tracking-[0.14em] uppercase text-white"
            style="background: linear-gradient(135deg, #A855F7 0%, #FF43A0 100%);"
          >
            {{ get('pages.ecommerce.badge') }}
          </span>

          <h1 class="mt-5 text-[2.15rem] sm:text-[2.65rem] lg:text-[3.15rem] font-bold tracking-[-0.035em] leading-[1.08] text-[color:var(--gw-ink)] text-balance">
            {{ get('pages.ecommerce.heading_line1') }}
            <span
              class="block mt-1 font-extrabold"
              style="background: linear-gradient(135deg, #F59754 0%, #FF43A0 52%, #F100FE 100%); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;"
            >{{ get('pages.ecommerce.heading_line2') }}</span>
          </h1>

          <p class="mt-5 text-[1.05rem] leading-[1.65] text-[rgba(51,51,51,0.68)] max-w-md text-pretty">
            {{ get('pages.ecommerce.lead') }}
          </p>

          <div class="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a href="https://admin.growiyo.com" class="gw-btn-grad touch-manipulation justify-center">
              {{ get('home.hero.cta_start') }}
            </a>
            <a
              :href="webRoute('web.demo')"
              :aria-label="get('home.hero.cta_watch_aria')"
              class="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full bg-white text-[color:var(--gw-ink)] text-sm font-semibold border border-[rgba(51,51,51,0.12)] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(241,0,254,0.35)] touch-manipulation"
            >
              <span
                class="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style="background: linear-gradient(135deg, #F59754 0%, #FF43A0 52%, #F100FE 100%);"
                aria-hidden="true"
              >
                <GwIcon name="play" class="w-3 h-3 text-white ml-0.5" />
              </span>
              {{ get('home.hero.cta_watch') }}
            </a>
          </div>

          <p class="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.875rem] text-[rgba(51,51,51,0.62)]">
            <template v-for="(item, i) in trustItems" :key="item">
              <span class="inline-flex items-center gap-1.5">
                <GwIcon name="check" class="w-3.5 h-3.5 text-[#22A06B] shrink-0" />
                {{ get(`pages.ecommerce.trust.${item}`) }}
              </span>
              <span v-if="i < trustItems.length - 1" class="text-[rgba(51,51,51,0.28)]" aria-hidden="true">•</span>
            </template>
          </p>
        </div>

        <!-- Visual stage -->
        <div class="lg:col-span-7 relative reveal reveal-delay-1 min-h-[360px] sm:min-h-[420px] lg:min-h-[480px]" aria-hidden="true">

          <!-- Dashboard (back) -->
          <div class="absolute left-0 top-6 sm:top-8 w-[58%] sm:w-[52%] rounded-2xl overflow-hidden shadow-[0_28px_60px_-24px_rgba(51,51,51,0.45)] border border-white/40 rotate-[-2deg]">
            <div class="flex bg-[#1E1B2E]">
              <div class="w-10 sm:w-12 bg-[#161322] py-3 flex flex-col items-center gap-2.5">
                <span class="w-5 h-5 rounded-md" style="background: linear-gradient(135deg, #F59754, #F100FE);"></span>
                <span v-for="n in 5" :key="n" class="w-4 h-4 rounded bg-white/10"></span>
              </div>
              <div class="flex-1 p-3 sm:p-4 bg-[#F8F7FC]">
                <div class="flex items-center justify-between mb-3">
                  <div>
                    <p class="text-[0.65rem] uppercase tracking-[0.16em] text-[rgba(51,51,51,0.45)] font-semibold">{{ get('pages.ecommerce.mock.orders') }}</p>
                    <p class="text-lg sm:text-xl font-bold text-[color:var(--gw-ink)] tracking-[-0.02em]">128</p>
                  </div>
                  <div class="text-right">
                    <p class="text-[0.65rem] uppercase tracking-[0.16em] text-[rgba(51,51,51,0.45)] font-semibold">{{ get('pages.ecommerce.mock.revenue') }}</p>
                    <p class="text-lg sm:text-xl font-bold text-[color:var(--gw-ink)] tracking-[-0.02em]">€24,860</p>
                  </div>
                </div>
                <svg class="w-full h-14 sm:h-16" viewBox="0 0 200 60" preserveAspectRatio="none">
                  <path d="M0 45 C30 40, 45 20, 70 28 C95 36, 110 12, 140 18 C165 23, 180 8, 200 14" fill="none" stroke="#22A06B" stroke-width="2.5"/>
                  <path d="M0 45 C30 40, 45 20, 70 28 C95 36, 110 12, 140 18 C165 23, 180 8, 200 14 L200 60 L0 60 Z" fill="rgba(34,160,107,0.12)"/>
                </svg>
                <div class="mt-2 space-y-1.5">
                  <div v-for="n in 3" :key="n" class="flex items-center gap-2">
                    <span class="w-6 h-6 rounded-md bg-[rgba(51,51,51,0.08)]"></span>
                    <span class="h-2 flex-1 rounded-full bg-[rgba(51,51,51,0.08)]"></span>
                    <span class="h-2 w-8 rounded-full bg-[rgba(51,51,51,0.06)]"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Storefront (middle) -->
          <div class="absolute left-[18%] sm:left-[22%] top-0 w-[70%] sm:w-[62%] rounded-2xl overflow-hidden bg-white shadow-[0_32px_70px_-20px_rgba(51,51,51,0.40)] border border-[rgba(51,51,51,0.08)] z-10">
            <div class="flex items-center gap-1.5 px-3 py-2 border-b border-[rgba(51,51,51,0.06)] bg-[#FAFAFA]">
              <span class="w-2 h-2 rounded-full bg-[#FF5F57]"></span>
              <span class="w-2 h-2 rounded-full bg-[#FEBC2E]"></span>
              <span class="w-2 h-2 rounded-full bg-[#28C840]"></span>
              <span class="ml-2 flex-1 h-5 rounded-md bg-[rgba(51,51,51,0.05)] text-[0.6rem] text-[rgba(51,51,51,0.4)] flex items-center px-2">yourstore.growiyo.com</span>
            </div>
            <div class="px-3 sm:px-4 py-2.5 flex items-center justify-between border-b border-[rgba(51,51,51,0.06)]">
              <span class="text-[0.75rem] sm:text-[0.85rem] font-bold tracking-[-0.02em] text-[color:var(--gw-ink)]">YourStore</span>
              <div class="hidden sm:flex items-center gap-3 text-[0.65rem] text-[rgba(51,51,51,0.55)]">
                <span>Home</span><span>Shop</span><span>Categories</span>
              </div>
              <div class="flex items-center gap-2 text-[rgba(51,51,51,0.45)]">
                <GwIcon name="search" class="w-3.5 h-3.5" />
                <span class="relative">
                  <GwIcon name="shopping-cart" class="w-3.5 h-3.5" />
                  <span class="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-[#FF43A0] text-[0.45rem] text-white flex items-center justify-center font-bold">1</span>
                </span>
              </div>
            </div>
            <div class="px-3 sm:px-4 py-3 flex gap-3 items-center bg-gradient-to-r from-[#FDF2F8] to-[#FAF5FF]">
              <div class="w-16 sm:w-20 h-16 sm:h-20 rounded-xl shrink-0" style="background: linear-gradient(145deg, #F9A8D4 0%, #F472B6 55%, #DB2777 100%);"></div>
              <div class="min-w-0">
                <p class="text-[0.65rem] uppercase tracking-[0.12em] text-[#FF43A0] font-semibold">{{ get('pages.ecommerce.mock.new_collection') }}</p>
                <p class="text-[0.8rem] sm:text-[0.9rem] font-bold text-[color:var(--gw-ink)] tracking-[-0.02em] leading-snug">{{ get('pages.ecommerce.mock.hero_tagline') }}</p>
                <span class="mt-1.5 inline-flex text-[0.65rem] font-semibold text-white px-2.5 py-1 rounded-full" style="background: linear-gradient(135deg, #FF43A0, #F100FE);">
                  {{ get('pages.ecommerce.mock.shop_now') }}
                </span>
              </div>
            </div>
            <div class="px-3 sm:px-4 py-3">
              <p class="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[rgba(51,51,51,0.45)] mb-2">{{ get('pages.ecommerce.mock.featured') }}</p>
              <div class="grid grid-cols-4 gap-1.5 sm:gap-2">
                <div v-for="product in storeProducts" :key="product.name" class="rounded-lg overflow-hidden border border-[rgba(51,51,51,0.06)]">
                  <div class="aspect-square" :style="{ background: product.tone }"></div>
                  <div class="p-1 sm:p-1.5">
                    <p class="text-[0.55rem] sm:text-[0.65rem] font-semibold text-[color:var(--gw-ink)] truncate">{{ product.name }}</p>
                    <p class="text-[0.55rem] sm:text-[0.6rem] text-[rgba(51,51,51,0.55)]">{{ product.price }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Live editor menu -->
          <div class="absolute left-[28%] sm:left-[32%] top-[42%] sm:top-[38%] z-20 rounded-xl bg-white border border-[rgba(51,51,51,0.08)] shadow-[0_16px_40px_-12px_rgba(51,51,51,0.35)] px-2.5 py-2 w-[9.5rem] sm:w-[10.5rem]">
            <div
              v-for="(item, i) in editorItems"
              :key="item"
              class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[0.7rem] font-medium text-[color:var(--gw-ink)]"
              :class="i === 0 ? 'bg-[rgba(255,67,160,0.08)] text-[#FF43A0]' : ''"
            >
              <span
                class="w-1.5 h-1.5 rounded-full shrink-0"
                :style="{ background: i === 0 ? '#FF43A0' : 'rgba(51,51,51,0.25)' }"
              ></span>
              {{ get(`pages.ecommerce.mock.editor.${item}`) }}
            </div>
          </div>

          <!-- Phone (front) -->
          <div class="absolute right-0 sm:right-2 bottom-0 sm:bottom-4 w-[28%] sm:w-[24%] max-w-[140px] z-30">
            <div class="rounded-[1.35rem] border-[3px] border-[#1E1B2E] bg-white shadow-[0_24px_50px_-16px_rgba(51,51,51,0.5)] overflow-hidden">
              <div class="bg-[#1E1B2E] h-4 flex items-center justify-center">
                <span class="w-10 h-1 rounded-full bg-white/25"></span>
              </div>
              <div class="p-1.5">
                <p class="text-[0.55rem] font-bold text-center text-[color:var(--gw-ink)] py-1">YourStore</p>
                <div class="rounded-lg overflow-hidden" style="background: linear-gradient(160deg, #F9A8D4, #F472B6);">
                  <div class="aspect-[4/3]"></div>
                </div>
                <div class="mt-1.5 grid grid-cols-2 gap-1">
                  <div v-for="n in 4" :key="n" class="rounded aspect-square" :style="{ background: storeProducts[n - 1].tone }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Value props strip -->
      <div class="mt-10 lg:mt-12 reveal">
        <ul class="flex flex-wrap items-center justify-center gap-3 lg:gap-4 list-none p-0 m-0">
          <li
            v-for="prop in valueProps"
            :key="prop.key"
            class="flex items-center gap-2.5 rounded-2xl bg-white/80 border border-[rgba(51,51,51,0.08)] px-3.5 py-2.5 shadow-[0_8px_24px_-16px_rgba(51,51,51,0.35)]"
          >
            <span
              class="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
              style="background: rgba(255,67,160,0.10); color: #FF43A0;"
              aria-hidden="true"
            >
              <GwIcon :name="prop.icon" class="w-4 h-4" />
            </span>
            <span class="min-w-0">
              <span class="block text-[0.8rem] font-semibold tracking-[-0.01em] text-[color:var(--gw-ink)] leading-tight">
                {{ get(`pages.ecommerce.value_props.${prop.key}.title`) }}
              </span>
              <span class="block text-[0.7rem] text-[rgba(51,51,51,0.55)] leading-tight">
                {{ get(`pages.ecommerce.value_props.${prop.key}.body`) }}
              </span>
            </span>
          </li>
        </ul>
      </div>

      <IncludedBackofficeCallout variant="ecommerce" class="mt-8 lg:mt-10" />

      <!-- Feature cards -->
      <div class="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 reveal">
        <article
          v-for="card in featureCards"
          :key="card.key"
          class="rounded-2xl bg-white border border-[rgba(51,51,51,0.08)] p-5 shadow-[0_12px_32px_-20px_rgba(51,51,51,0.35)] transition-transform duration-300 hover:-translate-y-1"
        >
          <span
            class="w-10 h-10 rounded-xl flex items-center justify-center"
            :style="{ background: card.accentSoft, color: card.accent }"
            aria-hidden="true"
          >
            <GwIcon :name="card.icon" class="w-5 h-5" />
          </span>
          <h2 class="mt-4 text-[0.95rem] font-bold tracking-[-0.015em] text-[color:var(--gw-ink)]">
            {{ get(`pages.ecommerce.features.${card.key}.title`) }}
          </h2>
          <p class="mt-1.5 text-[0.8rem] leading-[1.55] text-[rgba(51,51,51,0.62)]">
            {{ get(`pages.ecommerce.features.${card.key}.body`) }}
          </p>
        </article>
      </div>

      <!-- Devices note -->
      <div class="mt-10 lg:mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 reveal">
        <div class="flex items-center gap-3 text-[rgba(51,51,51,0.55)]">
          <span class="flex items-center gap-2.5" aria-hidden="true">
            <!-- Desktop -->
            <GwIcon name="device-desktop" class="w-5 h-5 shrink-0" />
            <!-- Tablet -->
            <GwIcon name="device-tablet" class="w-5 h-5 shrink-0" />
            <!-- Phone -->
            <GwIcon name="device-phone" class="w-5 h-5 shrink-0" />
          </span>
          <p class="text-[0.875rem] font-medium text-[color:var(--gw-ink)]">
            {{ get('pages.ecommerce.devices_note') }}
          </p>
        </div>
        <a :href="featuresHref" class="gw-btn-ghost">
          {{ get('pages.ecommerce.cta_features') }}
          <GwIcon name="arrow-right" class="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
</template>
